import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { FC, ReactNode, useState } from "react";
import type { SxProps, Theme } from "@mui/material";

import { divider, text } from "@/theme/colors";

type TCollapsibleCardProps = {
  title: ReactNode;
  description?: ReactNode;
  /** Optional right-aligned header content (e.g. a SegmentedControl). Clicks here do NOT toggle. */
  headerAction?: ReactNode;
  defaultExpanded?: boolean;
  children: ReactNode;
  /** Whether to show a border around the card. */
  showBorder?: boolean;
  sx?: SxProps<Theme>;
} & Omit<AccordionProps, "title" | "children">;

/**
 * Collapsible white card used across the 商品詳細 / 登録・編集 screens: bold title on the left,
 * an optional header action (e.g. segmented control) on the right, and a chevron that expands /
 * collapses the body. Composes MUI Accordion so behaviour/a11y come for free. Works both
 * controlled (pass `expanded` / `onChange`) and uncontrolled (defaults to expanded).
 */
export const CollapsibleCard: FC<TCollapsibleCardProps> = ({
  title,
  description,
  headerAction,
  defaultExpanded = true,
  children,
  showBorder = true,
  ...rest
}) => {
  const { expanded: expandedProp, onChange, ...accordionProps } = rest;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = expandedProp ?? internalExpanded;

  const handleChange: NonNullable<AccordionProps["onChange"]> = (event, expanded) => {
    if (expandedProp === undefined) {
      setInternalExpanded(expanded);
    }
    onChange?.(event, expanded);
  };

  return (
    <StyledAccordion
      disableGutters
      expanded={isExpanded}
      onChange={handleChange}
      elevation={0}
      showBorder={showBorder}
      {...accordionProps}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: text.primary }} />}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1} gap={2} pr={1.5}>
          <Stack gap={1}>
            {typeof title === "string" ? (
              <Typography variant="sub16Bold" color={text.primary}>
                {title}
              </Typography>
            ) : (
              title
            )}
            {description && (
              <Typography variant="cap12" color={text.secondary}>
                {description}
              </Typography>
            )}
          </Stack>
          {headerAction && (
            // Stop the click from toggling the accordion when interacting with the header action.
            <Box onClick={(e) => e.stopPropagation()} display={isExpanded ? "block" : "none"}>
              {headerAction}
            </Box>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </StyledAccordion>
  );
};

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== "showBorder",
})`
  background: #ffffff;
  border: ${(props: { showBorder: boolean }) => (props.showBorder ? `1px solid ${divider.middle}` : "none")};
  border-radius: 8px !important;
  padding: 24px 24px;
  &::before {
    display: none;
  }
  .MuiAccordionSummary-root,
  .MuiAccordionSummary-root.Mui-expanded {
    padding: 0 0;
    min-height: fit-content;
  }
  .MuiAccordionSummary-content,
  .MuiAccordionSummary-content.Mui-expanded {
    margin: 0;
  }
  .MuiAccordionDetails-root {
    padding: 24px 0 0;
  }
`;
